import api from "./api";
import { API_URL } from "../config";
import { getAuthToken } from "./authToken";

const unwrap = (res) => res.data?.data ?? res.data;

/** Dig through common IB/BaaS envelopes to find the batch array. */
const collectBatchItems = (node, depth = 0) => {
  if (node == null || depth > 5) return null;
  if (Array.isArray(node)) return node;
  if (typeof node !== "object") return null;
  if (Array.isArray(node.items)) return node.items;
  if (Array.isArray(node.Items)) return node.Items;
  if (node.data != null) return collectBatchItems(node.data, depth + 1);
  return null;
};

const normalizeBatchListResponse = (data) => {
  const root = data?.data ?? data;
  const list = collectBatchItems(root) ?? collectBatchItems(data) ?? [];
  const totalRaw = root?.total ?? root?.Total ?? data?.total ?? data?.Total;
  const total = Number(totalRaw);
  return {
    items: list,
    total: Number.isFinite(total) && total >= 0 ? total : list.length,
  };
};

export const listPixBatches = async ({ accountId } = {}) => {
  const params = {};
  if (accountId) params.account_id = accountId;
  const res = await api.get("/v1/ib/pix/batches", { params });
  return normalizeBatchListResponse(unwrap(res));
};

/**
 * Merge lotes across IB accounts (virtual + Conta Pagamento share one login).
 * Dedupes by batch_id and sorts newest first.
 */
export const listPixBatchesForAccounts = async (accountIds = []) => {
  const ids = [
    ...new Set(
      (Array.isArray(accountIds) ? accountIds : [])
        .map((id) => String(id || "").trim())
        .filter(Boolean),
    ),
  ];
  if (ids.length === 0) {
    return { items: [], total: 0 };
  }

  const results = await Promise.all(
    ids.map(async (accountId) => {
      try {
        const data = await listPixBatches({ accountId });
        return { ok: true, data };
      } catch (error) {
        return { ok: false, error };
      }
    }),
  );

  const byId = new Map();
  const errors = [];
  for (const result of results) {
    if (!result.ok) {
      errors.push(result.error);
      continue;
    }
    for (const item of result.data.items || []) {
      const batchId = item?.batch_id ?? item?.batchId;
      if (!batchId) continue;
      byId.set(String(batchId), item);
    }
  }

  const items = [...byId.values()].sort((a, b) => {
    const ta = new Date(a?.created_at ?? a?.createdAt ?? 0).getTime();
    const tb = new Date(b?.created_at ?? b?.createdAt ?? 0).getTime();
    return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
  });

  if (items.length === 0 && errors.length === ids.length && errors[0]) {
    throw errors[0];
  }

  return { items, total: items.length };
};

export const getPixBatch = async (batchId, { accountId } = {}) => {
  const params = {};
  if (accountId) params.account_id = accountId;
  const res = await api.get(`/v1/ib/pix/batches/${encodeURIComponent(batchId)}`, { params });
  return unwrap(res);
};

export const buildPixBatchStreamUrl = (batchId, { accountId } = {}) => {
  const base = String(API_URL || "").replace(/\/+$/, "");
  const params = new URLSearchParams();
  if (accountId) params.set("account_id", accountId);
  const qs = params.toString();
  return `${base}/v1/ib/pix/batches/${encodeURIComponent(batchId)}/stream${qs ? `?${qs}` : ""}`;
};

const parseSseChunk = (chunk, onEvent) => {
  const lines = String(chunk || "").split("\n");
  for (const line of lines) {
    if (!line.startsWith("data:")) continue;
    const payload = line.slice(5).trim();
    if (!payload) continue;
    try {
      onEvent?.(JSON.parse(payload), payload);
    } catch {
      onEvent?.(null, payload);
    }
  }
};

/**
 * SSE via fetch (Bearer auth — EventSource cannot send Authorization header).
 * Auto-reconnects with backoff until unsubscribe is called.
 * Returns unsubscribe function.
 */
export const subscribePixBatchStream = (batchId, { accountId, onEvent, onError } = {}) => {
  const url = buildPixBatchStreamUrl(batchId, { accountId });
  let closed = false;
  let attempt = 0;
  let controller = null;
  let reconnectTimer = null;

  const scheduleReconnect = () => {
    if (closed) return;
    attempt += 1;
    const delayMs = Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5));
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delayMs);
  };

  const connect = () => {
    if (closed) return;
    controller?.abort();
    controller = new AbortController();

    (async () => {
      try {
        const token = getAuthToken();
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`Stream failed (${res.status})`);
        }
        if (!res.body) {
          throw new Error("Stream body unavailable");
        }

        attempt = 0;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!closed) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() || "";
          for (const part of parts) {
            parseSseChunk(part, onEvent);
          }
        }
        if (buffer.trim()) {
          parseSseChunk(buffer, onEvent);
        }

        if (!closed) {
          scheduleReconnect();
        }
      } catch (err) {
        if (!closed && err?.name !== "AbortError") {
          onError?.(err);
          scheduleReconnect();
        }
      }
    })();
  };

  connect();

  return () => {
    closed = true;
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    controller?.abort();
    controller = null;
  };
};
